import { useState } from "react";
import { IoClose } from "react-icons/io5";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import { cn } from "@/lib/utils";
import { CreateAttendanceRequest } from "./comps/create-att-request";
import { MarkYourselfAbsent } from "./comps/mark-absent";

export type Attendance = {
  status: string;
  date: string | Date;
  id: string;
  checkinTime: string;
  checkoutTime: string;
};

interface Props {
  attendance: Attendance;
}

const ATTENDANCE_COLORS: {
  [key: string]: string;
} = {
  "Half-day": "#FFFF00",
  Present: "#00FF00",
  Absent: "#FF0000",
  "Paid Leave": "#00CED1",
  "On Leave": "#FFA500",
  "Not Counting": "#808080",
};

const ATTENDANCE_FOREGROUNDS: {
  [key: string]: string;
} = {
  "Half-day": "black",
  Present: "black",
  Absent: "black",
  "Paid Leave": "black",
  "On Leave": "black",
  "Not Counting": "black",
};

export const AttendanceEvent = ({ attendance }: Props) => {
  const [open, setOpen] = useState(false);

  const [step, setStep] = useState<"request" | "mark-absent" | "">("");

  const handleClose = () => {
    setStep("");
  };

  const colorAtt = ATTENDANCE_FOREGROUNDS[attendance.status];
  const bgAtt = ATTENDANCE_COLORS[attendance.status];

  return (
    <div
      style={{
        backgroundColor: bgAtt,
        color: colorAtt,
      }}
    >
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger className="tracking-normal">
          <div
            className={cn(
              "flex items-center justify-between px-2 py-1",
              colorAtt
            )}
          >
            <div className="text-sm">{attendance?.status}</div>
          </div>
        </AlertDialogTrigger>
        <AlertDialogContent
          className={cn("transition-all duration-300", step && "max-w-5xl")}
        >
          <AlertDialogCancel className="absolute right-2 top-2 h-7 w-7 p-0 border-none">
            <IoClose className="w-5 h-5" />
          </AlertDialogCancel>
          <AlertDialogHeader>
            <AlertDialogTitle>Attendance</AlertDialogTitle>
          </AlertDialogHeader>

          <div className="flex">
            <div className="flex flex-col p-3 shrink-0">
              <h5>
                <span>Attendance Marking : </span>
                <span className="font-semibold">{attendance?.status}</span>
              </h5>
              <h5 className="mt-1">
                <span>Checkin Time : </span>
                <span className="font-semibold">{attendance?.checkinTime}</span>
              </h5>
              <h5 className="mt-1">
                <span>Checkout Time : </span>
                <span className="font-semibold">
                  {attendance?.checkoutTime}
                </span>
              </h5>

              <div className="flex mt-1 flex-col gap-2">
                <p className="text-accent-foreground text-[13px] mt-5">
                  If you want to make an attendance request, then
                </p>

                <Button
                  size="sm"
                  onClick={() => {
                    if (step === "" || step !== "request") {
                      setStep("request");
                    } else {
                      setStep("");
                    }
                  }}
                >
                  Request Attendance
                </Button>
              </div>

              {attendance?.status === "Not Counting" && (
                <div className="flex mt-1 flex-col gap-2">
                  <p className="text-accent-foreground text-[13px] mt-5">
                    If you want to mark yourself absent, then
                  </p>

                  <Button
                    size="sm"
                    onClick={() => {
                      if (step === "" || step !== "mark-absent") {
                        setStep("mark-absent");
                      } else {
                        setStep("");
                      }
                    }}
                  >
                    Mark yourself absent
                  </Button>
                </div>
              )}
            </div>
            {step === "request" && (
              <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                <h2 className="text-lg font-medium">
                  Make an attendance request
                </h2>

                <CreateAttendanceRequest
                  attId={attendance?.id}
                  date={attendance?.date}
                  handleClose={handleClose}
                />
              </div>
            )}

            {step === "mark-absent" && (
              <div className="w-full border-l px-5 py-2 flex flex-col gap-3">
                <h2 className="text-lg font-medium">Mark yourself absent</h2>

                <MarkYourselfAbsent
                  attId={attendance?.id}
                  handleClose={handleClose}
                />
              </div>
            )}
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
